import LeadCaptureBox from "@/components/LeadCaptureBox";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, Zap, Check, ChevronRight, Shield, AlertTriangle, XCircle, CheckCircle } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const SCORE_LEVELS = [
  { range: "1–3", label: "Safe to send", color: "text-teal", bg: "bg-teal/10", border: "border-teal/20", desc: "No significant spam triggers detected. Ready to send." },
  { range: "4–6", label: "Review recommended", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", desc: "Some phrases may reduce deliverability. Review flagged items before sending." },
  { range: "7–10", label: "High risk", color: "text-red-500", bg: "bg-red-50", border: "border-red-200", desc: "Multiple spam triggers detected. Resolve before sending to protect sender reputation." },
];

const WHAT_IT_CATCHES = [
  { icon: AlertTriangle, label: "Trigger phrases", desc: "Words and combinations that Bayesian spam filters are trained on: \"FREE!!!\", \"Act now\", \"Limited time\", \"Guaranteed\", \"Click here\"." },
  { icon: XCircle, label: "ALL CAPS sections", desc: "Shouting triggers both algorithmic and human spam signals. The checker flags any run of 3+ consecutive uppercase words." },
  { icon: AlertTriangle, label: "Excessive punctuation", desc: "\"Don't miss this!!!!!!\" reads as spam to filters. Flagged when you have 2+ consecutive exclamation marks or question marks." },
  { icon: Shield, label: "Suspicious link patterns", desc: "Shortened URLs, mismatched anchor text, and bare IP address links all raise suspicion. Each flagged instance adds to your score." },
  { icon: AlertTriangle, label: "Image-to-text ratio", desc: "Emails that are mostly images with little text are a classic spam tactic. MoreMail checks your ratio and flags anything below 40% text." },
  { icon: XCircle, label: "Missing unsubscribe", desc: "No unsubscribe link is an instant high-severity flag — and a CAN-SPAM / GDPR violation. Caught before you can send." },
];

const SCORE_DEMO = [
  { phrase: "FREE upgrade", severity: "high", reason: "Trigger word in all-caps" },
  { phrase: "Click here!!!", severity: "high", reason: "Trigger phrase + excess punctuation" },
  { phrase: "Limited time offer", severity: "medium", reason: "Common spam phrase" },
  { phrase: "Unsubscribe link present", severity: "ok", reason: "Compliance check passed" },
  { phrase: "Text-to-image ratio: 68%", severity: "ok", reason: "Within safe threshold" },
];

export default function SpamScorePage() {
  return (
    <>
      <SeoHead
        title="Spam Score — MoreMail | Check Campaigns Before You Send"
        description="Before sending, every MoreMail campaign is scored 1-10 for spam risk. See exactly which phrases to change before they cost you inbox placement."
        url={`${SITE_URL}/spam-score`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Features", url: `${SITE_URL}/features` },
          { name: "Spam Score", url: `${SITE_URL}/spam-score` },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-coral/20 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <div className="flex items-center gap-1.5 text-white/40 text-xs font-medium mb-8">
            <Link to="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <span className="text-white/60">AI Suite</span>
            <ChevronRight size={12} />
            <span className="text-white/80">Spam Score</span>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral/20 border border-coral/30 rounded-full text-coral text-xs font-bold mb-6">
            <Zap size={11} />
            AI Suite
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5 max-w-3xl">
            Know your spam risk{" "}
            <em className="text-coral not-italic">before</em> you send.
          </h1>

          <p className="text-white/60 text-base sm:text-lg max-w-2xl mb-10 leading-relaxed">
            Every campaign gets a 1–10 spam risk score before sending. See exactly which phrases are flagged and fix them in seconds — before they cost you inbox placement.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <LeadCaptureBox />
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/20 text-white/70 font-semibold hover:border-white/40 hover:text-white transition-colors"
            >
              All features
            </Link>
          </div>
        </div>
      </section>

      {/* Score legend */}
      <section className="bg-white border-b border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
            What your score means
          </h2>
          <p className="text-slate text-sm text-center mb-10">The scale runs 1 (clean) to 10 (near-certain spam folder).</p>
          <div className="space-y-4">
            {SCORE_LEVELS.map((level) => (
              <div key={level.range} className={`rounded-2xl border p-5 ${level.bg} ${level.border}`}>
                <div className="flex items-start gap-4">
                  <div className={`font-display text-3xl font-bold flex-shrink-0 w-16 ${level.color}`}>
                    {level.range}
                  </div>
                  <div>
                    <h3 className={`font-bold text-sm mb-1 ${level.color}`}>{level.label}</h3>
                    <p className="text-slate text-sm">{level.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo checker output */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
          Exact flags, not vague warnings
        </h2>
        <p className="text-slate text-sm text-center mb-10">
          MoreMail doesn't just give you a number — it tells you precisely what to fix.
        </p>

        <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
          {/* Score header */}
          <div className="px-6 py-5 border-b border-black/[0.06] flex items-center justify-between bg-paper">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate mb-0.5">Campaign spam score</p>
              <p className="text-sm text-ink font-medium">"Q4 Product Announcement"</p>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl font-bold text-amber-500">6</div>
              <p className="text-xs text-amber-600 font-semibold">Review recommended</p>
            </div>
          </div>
          {/* Flag list */}
          <div className="divide-y divide-black/[0.04]">
            {SCORE_DEMO.map((item) => (
              <div key={item.phrase} className="px-6 py-4 flex items-center gap-4">
                <div className="flex-shrink-0">
                  {item.severity === "ok" ? (
                    <CheckCircle size={16} className="text-teal" />
                  ) : item.severity === "high" ? (
                    <XCircle size={16} className="text-red-400" />
                  ) : (
                    <AlertTriangle size={16} className="text-amber-400" />
                  )}
                </div>
                <div className="flex-1">
                  <span className={`text-sm font-medium ${
                    item.severity === "ok" ? "text-slate" : "text-ink"
                  }`}>
                    {item.phrase}
                  </span>
                </div>
                <div className={`text-xs font-medium flex-shrink-0 ${
                  item.severity === "ok" ? "text-teal" :
                  item.severity === "high" ? "text-red-400" : "text-amber-500"
                }`}>
                  {item.reason}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What it catches */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
            What gets checked
          </h2>
          <p className="text-slate text-sm text-center mb-10">Six categories scanned on every campaign before you can send.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHAT_IT_CATCHES.map((item) => (
              <div key={item.label} className="bg-paper rounded-2xl border border-black/[0.06] p-5 flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-coral/10 flex-shrink-0">
                  <item.icon size={16} className="text-coral" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-ink mb-1">{item.label}</h3>
                  <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
          Why this protects your sender reputation
        </h2>
        <p className="text-slate text-sm text-center mb-10 max-w-2xl mx-auto">
          Once your sending domain gets flagged, recovering takes weeks. Catching triggers before sending is far cheaper than rebuilding a damaged reputation.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          {[
            { stat: "72%", label: "of email never opened is due to spam folder placement" },
            { stat: "30 days", label: "minimum recovery time after a domain reputation hit" },
            { stat: "0 cost", label: "to fix a flagged phrase before sending" },
          ].map((item) => (
            <div key={item.stat} className="bg-paper rounded-2xl border border-black/[0.06] p-6">
              <div className="font-display text-3xl font-bold text-coral mb-2">{item.stat}</div>
              <p className="text-xs text-slate leading-relaxed">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="bg-white border-t border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-xl font-bold text-ink mb-6">Works great with</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "IP Blacklist Monitoring", href: "/features", desc: "Daily checks across 10 DNS blacklists so you know if your IP gets listed." },
              { label: "Custom Tracking Domains", href: "/features", desc: "Your own subdomain means tracking links don't inherit shared-domain spam penalties." },
              { label: "Inbox Preview", href: "/inbox-preview", desc: "See rendering in Gmail and Outlook before sending — paired with spam score for full pre-send peace of mind." },
            ].map((f) => (
              <Link
                key={f.label}
                to={f.href}
                className="bg-paper rounded-2xl border border-black/[0.06] p-5 hover:border-coral/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-bold text-sm text-ink mb-1.5 group-hover:text-coral transition-colors flex items-center justify-between">
                  {f.label} <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-xs text-slate leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
            Send with confidence.
          </h2>
          <p className="text-white/80 text-base mb-8">
            Know your spam risk before every send. Free on every plan.
          </p>
          <div className="flex justify-center"><LeadCaptureBox /></div>
        </div>
      </section>
    </>
  );
}
